import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Section = styled.section`
  padding: 80px 0;
  background-color: #f9fafb;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 2rem;
  color: #1f2937;

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const DiscussionNote = styled(motion.div)`
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border: 1px solid #f59e0b;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 3rem;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  p {
    margin: 0;
    color: #92400e;
    font-weight: 500;
    font-size: 1rem;
  }

  i {
    color: #f59e0b;
    margin-right: 0.5rem;
  }
`;

const TimelineContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e5e7eb;
    transform: translateX(-50%);

    @media (max-width: 768px) {
      left: 20px;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  display: flex;
  margin-bottom: 3rem;
  position: relative;

  &:nth-child(odd) {
    flex-direction: row-reverse;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding-left: 3rem;

    &:nth-child(odd) {
      flex-direction: column;
    }
  }
`;

const TimelineDate = styled.div`
  flex: 0 0 200px;
  text-align: center;
  padding: 1rem;
  background: #1e40af;
  color: white;
  border-radius: 8px;
  font-weight: 600;
  margin: 0 2rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    margin: 0 0 1rem 0;
    width: 150px;
  }
`;

const TimelineContent = styled.div`
  flex: 1;
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  border: 1px solid #e5e7eb;
`;

const TimelineTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #1f2937;
`;

const TimelineList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    padding: 0.5rem 0;
    color: #6b7280;
    position: relative;
    padding-left: 1.5rem;

    &::before {
      content: '•';
      position: absolute;
      left: 0;
      color: #1e40af;
      font-weight: 600;
    }
  }
`;

const Timeline = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const timelineData = [
    {
      quarter: 'Q1 2026',
      title: 'Foundation Building',
      items: [
        'Implement attendee feedback systems',
        'Secure keynote venue',
        'Begin marketing campaign planning'
      ]
    },
    {
      quarter: 'Q2 2026',
      title: 'Early Execution',
      items: [
        'Launch marketing campaign',
        'Establish accessibility solutions',
        'Begin partnership outreach'
      ]
    },
    {
      quarter: 'Q3 2026',
      title: 'Strategic Development',
      items: [
        'Implement thematic tracks',
        'Develop scheduling coordination',
        'Expand regional partnerships'
      ]
    },
    {
      quarter: 'Q4 2026',
      title: 'Tech Week 2026',
      items: [
        'Execute Tech Week 2026',
        'Collect comprehensive feedback',
        'Plan for 2027 improvements'
      ]
    }
  ];

  return (
    <Section id="timeline">
      <Container>
        <SectionTitle
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          12-Month Implementation Timeline
        </SectionTitle>
        
        <DiscussionNote
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <p>
            <i className="fas fa-calendar-alt"></i>
            This timeline is a suggested framework for committee discussion and planning. 
            Actual implementation dates will be determined by committee consensus.
          </p>
        </DiscussionNote>
        
        <TimelineContainer>
          {timelineData.map((item, index) => (
            <TimelineItem
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              <TimelineDate>{item.quarter}</TimelineDate>
              <TimelineContent>
                <TimelineTitle>{item.title}</TimelineTitle>
                <TimelineList>
                  {item.items.map((listItem, itemIndex) => (
                    <li key={itemIndex}>{listItem}</li>
                  ))}
                </TimelineList>
              </TimelineContent>
            </TimelineItem>
          ))}
        </TimelineContainer>
      </Container>
    </Section>
  );
};

export default Timeline;
